interface CacheItem<T> {
  data: T;
  expiresAt: number;
  staleUntil: number;
}
interface CacheOptions<T> {
  staleTtl?: number;
  shouldCache?: (value: T) => boolean;
}

export class ServerCache {
  private cache = new Map<string, CacheItem<any>>();
  private pending = new Map<string, Promise<any>>();

  // Public data only. Bounded stale-while-revalidate, one flight per key.
  async get<T>(key: string, fetcher: () => Promise<T>, ttl = 3600, timeoutMs = 14000, options: CacheOptions<T> = {}): Promise<T> {
    const now = Date.now();
    const cached = this.cache.get(key);
    if (cached && cached.expiresAt > now) return cached.data;
    if (cached && cached.staleUntil <= now) this.cache.delete(key);
    let flight = this.pending.get(key);
    if (!flight) {
      let timer: ReturnType<typeof setTimeout> | undefined;
      // Start synchronously while Nuxt's request context is available.
      let request: Promise<T>;
      try { request = Promise.resolve(fetcher()); } catch (error) { request = Promise.reject(error); }
      flight = (timeoutMs > 0 ? Promise.race([
        request,
        new Promise<T>((_, reject) => { timer = setTimeout(() => reject(new Error('Public cache refresh timed out')), timeoutMs); }),
      ]) : request).then(data => {
        // Invalidated/replaced flights cannot resurrect old data.
        if (this.pending.get(key) === flight && (!options.shouldCache || options.shouldCache(data))) {
          const completed = Date.now();
          this.cache.delete(key);
          this.cache.set(key, { data, expiresAt: completed + ttl * 1000, staleUntil: completed + (ttl + (options.staleTtl ?? 3600)) * 1000 });
          if (this.cache.size > 256) this.cache.delete(this.cache.keys().next().value!);
        }
        return data;
      }).finally(() => {
        clearTimeout(timer);
        if (this.pending.get(key) === flight) this.pending.delete(key);
      });
      this.pending.set(key, flight);
    }
    if (cached && cached.staleUntil > now) {
      void flight.catch(() => {});
      return cached.data;
    }
    return flight;
  }

  invalidate(key: string): boolean {
    this.pending.delete(key);
    return this.cache.delete(key);
  }

  invalidateAll(prefix?: string): void {
    for (const key of new Set([...this.cache.keys(), ...this.pending.keys()])) {
      if (!prefix || key.startsWith(prefix)) this.invalidate(key);
    }
  }
}

export const serverCache = new ServerCache();
